import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DollarSign, TrendingUp, TrendingDown, ShoppingCart, CreditCard, Banknote, QrCode, Gift, BarChart3, ArrowUpRight, ArrowDownRight, Wallet, Building2, Printer } from "lucide-react";

const formatPrice = (p) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(p);

const methodIcons = { cash: Banknote, qris: QrCode, edc: CreditCard, kupon: Gift };
const methodLabels = { cash: "Tunai", qris: "QRIS", edc: "EDC Debit/Kredit", kupon: "Kupon Loyalitas" };

export default function FinancePage() {
  const [period, setPeriod] = useState("daily");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/finance/report?period=${period}`); setReport(data); } catch {} finally { setLoading(false); }
  }, [period]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const printReport = () => {
    const r = report;
    if (!r) return;
    const periodLabel = { daily: "Harian", weekly: "Mingguan", monthly: "Bulanan", yearly: "Tahunan" }[period] || period;
    const today = new Date().toLocaleDateString("id-ID", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    const methodRows = (r.pos_by_method || []).map(m => `<tr><td style="padding:4px 8px;border-bottom:1px solid #ddd">${methodLabels[m.method] || m.method}</td><td style="padding:4px 8px;border-bottom:1px solid #ddd;text-align:right">${m.count} tx</td><td style="padding:4px 8px;border-bottom:1px solid #ddd;text-align:right;font-family:monospace">${formatPrice(m.total)}</td></tr>`).join("");
    const chartRows = (r.chart_data || []).map(d => `<tr><td style="padding:3px 8px;border-bottom:1px solid #eee">${d.label}</td><td style="padding:3px 8px;border-bottom:1px solid #eee;text-align:right">${d.count}</td><td style="padding:3px 8px;border-bottom:1px solid #eee;text-align:right;font-family:monospace">${formatPrice(d.revenue)}</td></tr>`).join("");
    const txRows = (r.transactions || []).map(t => {
      const time = t.created_at ? new Date(t.created_at).toLocaleString("id-ID", {day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"}) : "-";
      return `<tr><td style="padding:3px 6px;border-bottom:1px solid #eee;font-size:9px">${time}</td><td style="padding:3px 6px;border-bottom:1px solid #eee;font-family:monospace;font-size:9px">${t.transaction_code}</td><td style="padding:3px 6px;border-bottom:1px solid #eee">${t.customer_name}</td><td style="padding:3px 6px;border-bottom:1px solid #eee;font-size:9px">${t.items}</td><td style="padding:3px 6px;border-bottom:1px solid #eee;font-size:9px">${t.cashier_name}</td><td style="padding:3px 6px;border-bottom:1px solid #eee;font-size:9px;text-transform:uppercase">${methodLabels[t.payment_method] || t.payment_method}</td><td style="padding:3px 6px;border-bottom:1px solid #eee;text-align:right;font-family:monospace">${formatPrice(t.total)}</td></tr>`;
    }).join("");
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>Laporan Keuangan ${periodLabel}</title>
<style>@page{size:A4;margin:12mm}*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Segoe UI',sans-serif;font-size:10px;color:#000;background:#fff;padding:12mm}h1{font-size:16px;text-align:center;margin-bottom:2px}.sub{text-align:center;font-size:10px;color:#666;margin-bottom:12px}.date{text-align:center;font-size:11px;font-weight:bold;margin-bottom:16px;padding:6px;background:#f5f5f5;border:1px solid #ddd}table{width:100%;border-collapse:collapse;margin-bottom:12px}th{background:#f0f0f0;padding:5px 8px;text-align:left;font-size:9px;text-transform:uppercase;border-bottom:2px solid #333}.section{margin-bottom:14px;page-break-inside:avoid}.section-title{font-size:10px;font-weight:bold;text-transform:uppercase;letter-spacing:1px;margin-bottom:5px;padding-bottom:3px;border-bottom:1px solid #333}.total-row td{font-weight:bold;border-top:2px solid #333}.sig-area{margin-top:30px;display:flex;justify-content:space-between;gap:40px}.sig-box{flex:1;text-align:center;padding-top:60px;border-top:1px solid #333}.sig-label{font-size:10px;color:#666;margin-top:4px}</style></head><body>
<h1>FI BARBERSHOP SEKIP</h1><div class="sub">Laporan Keuangan ${periodLabel}</div><div class="date">${today}</div>
<div class="section"><div class="section-title">Ringkasan</div><table>
<tr><td>Pemasukan Kotor</td><td style="text-align:right;font-family:monospace;font-weight:bold">${formatPrice(r.gross_income)}</td></tr>
<tr><td>POS Kasir (${r.pos_transactions} tx)</td><td style="text-align:right;font-family:monospace">${formatPrice(r.pos_revenue)}</td></tr>
<tr><td>Toko Online (${r.online_orders} pesanan)</td><td style="text-align:right;font-family:monospace">${formatPrice(r.online_revenue)}</td></tr>
<tr><td>Layanan</td><td style="text-align:right;font-family:monospace">${formatPrice(r.service_revenue)}</td></tr>
<tr><td>Produk</td><td style="text-align:right;font-family:monospace">${formatPrice(r.product_revenue)}</td></tr>
<tr><td style="color:red">Refund (${r.refunds_count}x)</td><td style="text-align:right;font-family:monospace;color:red">-${formatPrice(r.refund_total)}</td></tr>
<tr class="total-row"><td><strong>PENDAPATAN BERSIH</strong></td><td style="text-align:right;font-family:monospace;font-size:13px"><strong>${formatPrice(r.net_income)}</strong></td></tr>
</table></div>
<div class="section"><div class="section-title">Metode Pembayaran</div><table><thead><tr><th>Metode</th><th style="text-align:right">Jumlah</th><th style="text-align:right">Total</th></tr></thead><tbody>${methodRows}</tbody></table></div>
<div class="section"><div class="section-title">Pembukuan Per Periode</div><table><thead><tr><th>Periode</th><th style="text-align:right">Transaksi</th><th style="text-align:right">Pemasukan</th></tr></thead><tbody>${chartRows}</tbody><tfoot><tr class="total-row"><td>Total</td><td style="text-align:right">${r.pos_transactions}</td><td style="text-align:right;font-family:monospace">${formatPrice(r.pos_revenue)}</td></tr></tfoot></table></div>
${txRows ? `<div class="section"><div class="section-title">Detail Transaksi (${r.transactions?.length || 0})</div><table><thead><tr><th>Waktu</th><th>Kode</th><th>Pelanggan</th><th>Item</th><th>Kasir</th><th>Metode</th><th style="text-align:right">Total</th></tr></thead><tbody>${txRows}</tbody></table></div>` : ""}
<div class="sig-area"><div class="sig-box"><div class="sig-label">Finance</div><div style="margin-top:4px;font-size:9px;color:#999">( __________________ )</div></div><div class="sig-box"><div class="sig-label">Kepala Cabang</div><div style="margin-top:4px;font-size:9px;color:#999">( __________________ )</div></div></div>
</body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-[#E60000] border-t-transparent rounded-full animate-spin" /></div>;
  if (!report) return <div className="p-8 text-gray-500 text-center">Gagal memuat laporan</div>;

  const maxRevenue = Math.max(...(report.chart_data || []).map(d => d.revenue), 1);

  return (
    <div className="p-6 md:p-8" data-testid="finance-page">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-white">Keuangan</h1>
          <p className="text-gray-400 text-sm mt-1">Laporan pemasukan, pengeluaran, dan audit keuangan</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="bg-[#1A1A1A] border-[#262626] text-white h-9 rounded-none w-40 text-xs" data-testid="period-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#262626]">
              <SelectItem value="daily" className="text-white text-xs">Harian (Hari Ini)</SelectItem>
              <SelectItem value="weekly" className="text-white text-xs">Mingguan</SelectItem>
              <SelectItem value="monthly" className="text-white text-xs">Bulanan</SelectItem>
              <SelectItem value="yearly" className="text-white text-xs">Tahunan</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={printReport} className="bg-[#262626] hover:bg-[#333] text-white h-9 rounded-none text-xs px-3 no-print" data-testid="print-report"><Printer size={14} className="mr-1" /> Cetak</Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <SummaryCard icon={TrendingUp} label="Total Pemasukan" value={formatPrice(report.gross_income)} color="text-green-400" bg="bg-green-900/10 border-green-800/30" />
        <SummaryCard icon={ShoppingCart} label="POS Kasir" value={formatPrice(report.pos_revenue)} sub={`${report.pos_transactions} transaksi`} color="text-blue-400" bg="bg-blue-900/10 border-blue-800/30" />
        <SummaryCard icon={ArrowDownRight} label="Refund" value={formatPrice(report.refund_total || 0)} sub={`${report.refunds_count} disetujui`} color="text-red-400" bg="bg-red-900/10 border-red-800/30" />
        <SummaryCard icon={TrendingDown} label="Pendapatan Bersih" value={formatPrice(report.net_income)} color={report.net_income >= 0 ? "text-green-400" : "text-red-400"} bg={report.net_income >= 0 ? "bg-green-900/10 border-green-800/30" : "bg-red-900/10 border-red-800/30"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Revenue Chart */}
        <div className="lg:col-span-2 bg-[#0F0F0F] border border-[#262626] p-5" data-testid="revenue-chart">
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4 flex items-center gap-1.5"><BarChart3 size={14} /> Grafik Pemasukan ({period === "daily" ? "Per Jam" : period === "weekly" ? "Per Hari" : period === "monthly" ? "Per Tanggal" : "Per Bulan"})</h2>
          <div className="flex items-end gap-1 h-40">
            {(report.chart_data || []).map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                <div className="w-full bg-[#E60000]/20 relative" style={{ height: `${Math.max(2, (d.revenue / maxRevenue) * 100)}%` }}>
                  <div className="absolute inset-0 bg-[#E60000]" style={{ height: "100%" }} />
                </div>
                <span className="text-[8px] text-gray-500 truncate w-full text-center">{d.label}</span>
              </div>
            ))}
            {(report.chart_data || []).length === 0 && <p className="text-gray-600 text-xs w-full text-center py-8">Belum ada data</p>}
          </div>
        </div>

        {/* Payment Method Breakdown */}
        <div className="bg-[#0F0F0F] border border-[#262626] p-5" data-testid="payment-breakdown">
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4 flex items-center gap-1.5"><CreditCard size={14} /> Metode Pembayaran</h2>
          <div className="space-y-3">
            {(report.pos_by_method || []).length === 0 && <p className="text-gray-600 text-xs text-center py-4">Belum ada transaksi</p>}
            {(report.pos_by_method || []).map((m, i) => {
              const Icon = methodIcons[m.method] || Banknote;
              const pct = report.pos_revenue > 0 ? ((m.total / report.pos_revenue) * 100).toFixed(1) : 0;
              return (
                <div key={i} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon size={14} className="text-gray-400" />
                      <span className="text-white text-xs font-semibold">{methodLabels[m.method] || m.method}</span>
                    </div>
                    <span className="text-white text-xs font-mono">{formatPrice(m.total)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-[#262626]">
                      <div className="h-full bg-[#E60000]" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-gray-500 text-[10px] w-12 text-right">{pct}%</span>
                  </div>
                  <p className="text-gray-500 text-[10px]">{m.count} transaksi</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Detail Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Revenue by Category */}
        <div className="bg-[#0F0F0F] border border-[#262626] p-5" data-testid="category-breakdown">
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4 flex items-center gap-1.5"><DollarSign size={14} /> Rincian Pemasukan</h2>
          <div className="space-y-2.5">
            <DetailRow label="Pendapatan Jasa/Layanan (POS)" value={formatPrice(report.service_revenue)} />
            <DetailRow label="Pendapatan Produk (POS + Online)" value={formatPrice(report.product_revenue)} />
            <DetailRow label="Ongkir Terkumpul (Online)" value={formatPrice(report.online_shipping_collected)} />
            <DetailRow label="Toko Online" value={formatPrice(report.online_revenue)} />
            <div className="border-t border-[#262626] pt-2.5 mt-2.5">
              <DetailRow label="Pemasukan Kotor" value={formatPrice(report.gross_income)} bold />
            </div>
            {(report.refund_total || 0) > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-red-400 text-xs">Refund ({report.refunds_count}x)</span>
                <span className="text-red-400 text-xs font-mono">-{formatPrice(report.refund_total)}</span>
              </div>
            )}
            <div className="border-t border-[#262626] pt-2.5 mt-2.5">
              <DetailRow label="Pendapatan Bersih" value={formatPrice(report.net_income)} bold />
            </div>
          </div>
        </div>

        {/* Audit Section */}
        <div className="bg-[#0F0F0F] border border-[#262626] p-5" data-testid="audit-section">
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4 flex items-center gap-1.5"><Building2 size={14} /> Audit Keuangan</h2>
          <div className="space-y-2.5">
            {(report.pos_by_method || []).map((m, i) => {
              const label = m.method === "cash" ? "Kas Fisik (Tunai)" : m.method === "qris" ? "Rekening Digital (QRIS)" : m.method === "edc" ? "Rekening Bank (EDC)" : m.method === "kupon" ? "Kupon (Non-Cash)" : m.method;
              const icon = m.method === "cash" ? Wallet : m.method === "edc" ? Building2 : m.method === "qris" ? QrCode : Gift;
              return <AuditRow key={i} icon={icon} label={label} amount={formatPrice(m.total)} count={m.count} />;
            })}
            <div className="border-t border-[#262626] pt-2.5 mt-2.5">
              <DetailRow label="Online Store (Bank/QRIS)" value={formatPrice(report.online_revenue)} />
            </div>
            <div className="border-t border-[#262626] pt-2.5 mt-2.5">
              <div className="flex justify-between items-center">
                <span className="text-white text-sm font-bold">Total Seluruh Pemasukan</span>
                <span className="text-[#E60000] text-sm font-bold font-mono">{formatPrice(report.gross_income)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pembukuan Detail */}
      <div className="bg-[#0F0F0F] border border-[#262626] p-5 mb-6" data-testid="bookkeeping">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4 flex items-center gap-1.5"><TrendingUp size={14} /> Pembukuan {period === "daily" ? "Hari Ini" : period === "weekly" ? "Minggu Ini" : period === "monthly" ? "Bulan Ini" : "Tahun Ini"}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b border-[#262626]">
              <th className="text-left px-3 py-2 text-gray-500 font-bold uppercase">Periode</th>
              <th className="text-right px-3 py-2 text-gray-500 font-bold uppercase">Transaksi</th>
              <th className="text-right px-3 py-2 text-gray-500 font-bold uppercase">Pemasukan</th>
            </tr></thead>
            <tbody>
              {(report.chart_data || []).map((d, i) => (
                <tr key={i} className="border-b border-[#262626]/30 hover:bg-[#1A1A1A]">
                  <td className="px-3 py-2 text-white">{d.label}</td>
                  <td className="px-3 py-2 text-gray-400 text-right">{d.count}</td>
                  <td className="px-3 py-2 text-right font-mono text-green-400">{formatPrice(d.revenue)}</td>
                </tr>
              ))}
              {(report.chart_data || []).length === 0 && <tr><td colSpan={3} className="px-3 py-6 text-center text-gray-600">Belum ada data</td></tr>}
            </tbody>
            <tfoot><tr className="border-t border-[#262626]">
              <td className="px-3 py-2 text-white font-bold">Total</td>
              <td className="px-3 py-2 text-white text-right font-bold">{report.pos_transactions}</td>
              <td className="px-3 py-2 text-right font-bold font-mono text-[#E60000]">{formatPrice(report.pos_revenue)}</td>
            </tr></tfoot>
          </table>
        </div>
      </div>

      {/* Detail Transaksi */}
      {(report.transactions || []).length > 0 && (
        <div className="bg-[#0F0F0F] border border-[#262626] p-5" data-testid="finance-tx-detail">
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4 flex items-center gap-1.5"><ShoppingCart size={14} /> Detail Transaksi ({report.transactions.length})</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead><tr className="border-b border-[#262626]">
                <th className="text-left px-2 py-2 text-gray-500 font-bold uppercase">Waktu</th>
                <th className="text-left px-2 py-2 text-gray-500 font-bold uppercase">Kode</th>
                <th className="text-left px-2 py-2 text-gray-500 font-bold uppercase">Pelanggan</th>
                <th className="text-left px-2 py-2 text-gray-500 font-bold uppercase">Item</th>
                <th className="text-left px-2 py-2 text-gray-500 font-bold uppercase">Kasir</th>
                <th className="text-left px-2 py-2 text-gray-500 font-bold uppercase">Metode</th>
                <th className="text-right px-2 py-2 text-gray-500 font-bold uppercase">Total</th>
              </tr></thead>
              <tbody>
                {report.transactions.map((t, i) => (
                  <tr key={i} className="border-b border-[#262626]/30 hover:bg-[#1A1A1A]">
                    <td className="px-2 py-1.5 text-gray-400">{t.created_at ? new Date(t.created_at).toLocaleString("id-ID", {day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"}) : "-"}</td>
                    <td className="px-2 py-1.5 text-white font-mono text-[10px]">{t.transaction_code}</td>
                    <td className="px-2 py-1.5 text-white">{t.customer_name}</td>
                    <td className="px-2 py-1.5 text-gray-300 max-w-[200px] truncate">{t.items}</td>
                    <td className="px-2 py-1.5 text-gray-400">{t.cashier_name}</td>
                    <td className="px-2 py-1.5 text-gray-400 uppercase text-[10px]">{methodLabels[t.payment_method] || t.payment_method}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-green-400">{formatPrice(t.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, sub, color, bg }) {
  return (
    <div className={`border p-4 ${bg}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className={color} />
        <span className="text-gray-400 text-[10px] font-bold uppercase tracking-wider">{label}</span>
      </div>
      <p className={`font-heading text-xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-gray-500 text-[10px] mt-0.5">{sub}</p>}
    </div>
  );
}

function DetailRow({ label, value, bold }) {
  return (
    <div className="flex justify-between items-center">
      <span className={`text-xs ${bold ? "text-white font-bold" : "text-gray-400"}`}>{label}</span>
      <span className={`text-xs font-mono ${bold ? "text-[#E60000] font-bold" : "text-white"}`}>{value}</span>
    </div>
  );
}

function AuditRow({ icon: Icon, label, amount, count }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon size={14} className="text-gray-500" />
        <div>
          <p className="text-white text-xs">{label}</p>
          <p className="text-gray-500 text-[10px]">{count} transaksi</p>
        </div>
      </div>
      <span className="text-white text-xs font-mono">{amount}</span>
    </div>
  );
}
